
import MBDatatablePlus from "@/components/mb-datatable/DataTablePlus.vue";
import { computed, onActivated, onMounted, onUpdated, ref } from "vue";
import {
  FilterOption,
  SortCounter,
  SortOption,
  TableHeader,
} from "@/core/directive/interface/common";
import { useI18n } from "vue-i18n";
import {
  formatUtcDate,
  setModuleBCN,
  settlementLastDayDate,
} from "@/core/directive/function/common";
import { useRoute } from "vue-router";
import { ApiKyc } from "@/core/api";
import _ from "lodash";
import { useComputedFn } from "@/core/directive/function/order";
import { CommonDateType } from "@/core/directive/type/common";
import { CancelTokenSource } from "axios";
import {
  getKycStatus,
  getKycCommonTypeParams,
  getKycVerificationTypeParams,
} from "@/core/directive/function/kyc";
import { MenuComponent } from "@/assets/ts/components";
import { KycVerificationListItem } from "@/core/directive/interface/kyc";

import AddKycModal from "./AddKycModal.vue";
import { KycType } from "@/core/directive/type/kyc";

export default {
  name: "kyc-verification-list",
  components: {
    MBDatatablePlus,
    AddKycModal,
  },
  setup() {
    const route = useRoute();
    const { t } = useI18n();
    const tableData = ref<Array<KycVerificationListItem>>([]);
    const currentPage = ref<number>(1);
    const total = ref<number>(1);
    const pageSize = ref<number>(10);
    const sortOrder = ref("desc");
    const sortLabel = ref("");
    const tableLoading = ref(false);
    const search = ref<string>("");
    const sortOptions = ref<Array<SortOption>>([
      { field: "id", direction: "desc" },
    ]);
    const filterOptions = ref<Array<FilterOption>>([]);

    const tableHeader = computed(() => {
      let arr: TableHeader[] = [
        {
          name: t("kyc.list.id"),
          key: "id",
        },
      ];
      if (getKycCommonTypeParams.value.entityType === KycType.Juridical) {
        arr.push({
          name: t("kyc.list.companyName"),
          key: "company_name",
        });
      }
      if (getKycCommonTypeParams.value.entityType === KycType.Natural) {
        arr.push({
          name: t("kyc.list.name"),
          key: "lastname",
        });
      }
      arr.push(
        {
          name: t("kyc.list.lastUpdateTime"),
          key: "last_saved_at",
        },
        {
          name: t("common.actions"),
          key: "actions",
          sortable: false,
          align: "right",
        }
      );
      return arr;
    });

    const options = ref({
      defaultSort: [...sortOptions.value] as SortOption[],
      filter_group: [] as FilterOption[],
      pick_date_range: [] as CommonDateType[],
      cancelSource: null as CancelTokenSource | null,
    });

    const getDateType = computed(() => {
      let format = "YYYY-MM-DD",
        valueFormat = "YYYY-MM-DD";
      return {
        format,
        valueFormat,
      };
    });

    const allDate = [
      new Date("2022-02-01T00:00:00.000Z"),
      settlementLastDayDate({
        value: 0,
        isStart: false,
        format: getDateType.value.valueFormat,
      }),
    ];

    const disabledDate = (time: Date) => {
      const startDate = new Date("2022-01-31");
      return (
        time.getTime() > Date.now() || time.getTime() < startDate.getTime()
      );
    };

    const defaultDate = () => {
      // options.value.pick_date_range = [
      //   new Date("2022-02-01T00:00:00.000Z").toISOString(),
      //   settlementLastDayDate({
      //     value: 0,
      //     isStart: false,
      //     format: getDateType.value.valueFormat,
      //   }),
      // ];
    };

    const setOtherFilter = computed(() => {
      let arr: FilterOption[] = [];
      if (
        options.value.pick_date_range &&
        options.value.pick_date_range.length > 0
      ) {
        arr.push({
          field: "last_saved_at",
          value: options.value.pick_date_range[0]!,
          condition: "gteq",
        });
        arr.push({
          field: "last_saved_at",
          value: options.value.pick_date_range[1]!,
          condition: "lteq",
        });
      }
      return arr;
    });

    const setCommonFilter = computed(() => {
      return {
        page: currentPage.value,
        page_size: pageSize.value,
        search_value: search.value,
        sort_orders: sortOptions.value,
        filter_group: [...filterOptions.value, ...setOtherFilter.value],
      };
    });

    /**
     * @description: 请求数据
     */
    const getList = async () => {
      tableLoading.value = true;

      const { data } = await ApiKyc.getKycVerificationList({
        ...setCommonFilter.value,
        type: getKycCommonTypeParams.value.entityType,
      });
      tableLoading.value = false;
      if (data.code === 0) {
        tableData.value.splice(0, tableData.value.length, ...data.data.items);
        total.value = data.data.total;
      }
    };

    /**
     * @description: 渲染：状态
     */
    const getStatus = useComputedFn(getKycStatus);

    /**
     * @description: 获取列表
     */
    const updateList = () => {
      currentPage.value = 1;
      getList();
    };

    /**
     * @description: 重置过滤器（只修改参数）
     */
    const resetFilter = () => {
      filterOptions.value.splice(0, filterOptions.value.length);
    };

    /**
     * @description: 重置过滤器（拉取数据）
     */
    const handleFilterReset = () => {
      resetFilter();
      updateList();
    };

    /**
     * @description: 重置排序（拉取数据）
     */
    const handleSortReset = () => {
      resetColumnSort();
      updateList();
    };

    /**
     * @description: 重置排序（只修改参数）
     */
    const resetColumnSort = () => {
      sortOptions.value.splice(
        0,
        sortOptions.value.length,
        ...options.value.defaultSort
      );
      sortOrder.value = "desc";
      sortLabel.value = "";
    };

    /**
     * @description: 切换页码时
     */
    const onCurrentPageChange = (page) => {
      currentPage.value = page;
      getList();
    };

    /**
     * @description: 切换记录条数时
     */
    const onRowsPerPageChange = (pagesize) => {
      pageSize.value = pagesize;
      updateList();
    };

    const sortCounterFlag = [] as SortCounter[];

    /**
     * @description: 排序独立计数
     */
    const sortCounter = (field: string) => {
      if (sortCounterFlag.some((item) => item.field === field)) {
        sortCounterFlag.forEach((item) => {
          if (item.field === field) {
            item.counter = item.counter + 1;
          }
        });
      } else {
        sortCounterFlag.splice(0, sortCounterFlag.length, {
          field: field,
          counter: 1,
        });
      }
    };

    /**
     * @description: 排序3次判断
     */
    const onSortCounterThree = (val) => {
      let resetFlag = false;
      sortCounterFlag.forEach((item) => {
        if (item.field === val.columnName) {
          if (item.counter === 3) {
            sortCounterFlag.splice(0, sortCounterFlag.length);
            resetFlag = true;
          }
        }
      });
      return resetFlag;
    };

    /**
     * @description: 切换日期选择器时
     */
    const handleFilter = (val) => {
      resetColumnSort(); // 重置排序
      updateList();
    };

    /**
     * @description: 切换状态筛选器时
     */
    const handleSourceSelect = (val) => {
      resetFilter();
      resetColumnSort();
      updateList();
    };

    /**
     * @description: 排序时
     */
    const onColumnSort = (val: any) => {
      sortCounter(val.columnName);
      const resetFlag = onSortCounterThree(val);
      if (resetFlag) {
        handleSortReset();
      } else {
        let thisDirection = "";
        sortCounterFlag.forEach((item) => {
          if (item.field === val.columnName) {
            if (item.counter === 1) {
              thisDirection = "asc"; //第一次正序
            }
            if (item.counter === 2) {
              thisDirection = "desc"; //第二次倒序
            }
          }
        });
        sortOrder.value = thisDirection;
        const newSortRule = [] as SortOption[];
        newSortRule.push({
          field: val.columnName,
          direction: thisDirection,
        });
        sortOptions.value.splice(0, sortOptions.value.length, ...newSortRule);
        sortLabel.value = val.columnName;
        updateList();
      }
    };

    /**
     * @description: 搜索时
     */
    const debounceSearch = _.debounce(updateList, 1000);
    const searchItems = () => {
      debounceSearch();
    };
    const removeSearch = () => {
      search.value = "";
      updateList();
    };

    const goPath = computed(() => {
      return getKycVerificationTypeParams.value.routerUrl;
    });

    const getJumpRoutes = (item: KycVerificationListItem) => {
      const id = item.id;
      return goPath.value + "/" + id + "/information";
    };

    const init = () => {
      setModuleBCN(t, route);
      updateList();
    };

    onMounted(() => {
      options.value.defaultSort.splice(
        0,
        options.value.defaultSort.length,
        ...sortOptions.value
      );
      defaultDate();
      options.value.filter_group = [...setCommonFilter.value.filter_group];
      init();
      MenuComponent.reinitialization();
    });

    onActivated(() => {
      init();
    });

    onUpdated(() => {
      MenuComponent.reinitialization();
    });

    return {
      t,
      formatUtcDate,
      tableData,
      getStatus,
      currentPage,
      total,
      pageSize,
      search,
      tableHeader,
      sortOrder,
      sortLabel,
      options,
      onCurrentPageChange,
      onRowsPerPageChange,
      searchItems,
      removeSearch,
      tableLoading,
      onColumnSort,
      getDateType,
      handleFilter,
      handleFilterReset,
      handleSortReset,
      resetFilter,
      resetColumnSort,
      setCommonFilter,
      setOtherFilter,
      updateList,
      disabledDate,
      handleSourceSelect,
      //getServiceManagementTypeParams,
      goPath,
      getJumpRoutes,
    };
  },
};
